import { useCallback, useMemo, useState } from 'react';
import { FormApiInterface, Validator } from 'react-uforms';
import { Question, questionFieldType, QuestionRequired, ValidationTypes } from '@ch/common/types/data';
import { useData } from '../../use-data';
import { newFormatData } from '../new-format-data';
import { FieldGroupsData } from '../types';
import { useLang } from '../../use-lang';
import { composeQuestions, isSpecialQuestion } from '../helpers';
import { emailRegexp } from '../const';
import {
  validationMessage,
  minNumberValidation,
  maxNumberValidation,
  ageValidation,
  dateValidation,
  DateComparisonMode,
} from '../helpers/validators';

export const useQuiz = () => {
  const { lang } = useLang();

  const [pregnancyValues, setPregnancyValues] = useState({});
  const [pregnancyFile, setPregnancyFile] = useState<File>();
  const [documents, setDocuments] = useState([]);
  const [validation, setValidation] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const {
    data: {
      personaQuestion,
      eventQuestion,
      questions,
      mandatory: { mandatory_message: requiredMessage },
    },
  } = useData();

  const [userQuestions, setUserQuestions] = useState<Question[]>(questions);

  const cleanupValues = useCallback((questions: Question[], { setAllValues, getAllValues }: FormApiInterface) => {
    const newValues = questions.reduce((acc, question) => {
      if (!isSpecialQuestion(question) && question.hidden) {
        acc[question.identifier] = question.value;
      }
      return acc;
    }, getAllValues());
    setAllValues(newValues);
  }, []);

  const questionValidation = useCallback(
    ({ identifier, required, validations }: Question, api: FormApiInterface) => ({
      [identifier]: [
        ...(required === QuestionRequired.YES ? [Validator.Required(requiredMessage)] : []),
        ...(validations.length > 0
          ? [
              ...validations.map(item => {
                switch (item.type) {
                  case ValidationTypes.EMAIL:
                    return Validator.Preg(
                      new RegExp(emailRegexp),
                      validationMessage(item.message, `Please enter a valid email address`),
                    );
                  case ValidationTypes.FLOAT:
                    return Validator.FloatNumber(validationMessage(item.message, `Only numbers`));
                  case ValidationTypes.INTEGER:
                    return Validator.IntegerNumber(validationMessage(item.message, `Only integer numbers`));
                  case ValidationTypes.MAXLENGTH:
                    return Validator.MaxLength(
                      +item.value,
                      validationMessage(item.message, `The maximum character length is ${item.value} characters`),
                    );
                  case ValidationTypes.MINLENGTH:
                    return Validator.MinLength(
                      +item.value,
                      validationMessage(item.message, `The minimum character length is ${item.value} characters`),
                    );
                  case ValidationTypes.MINNUMBER:
                    return minNumberValidation(
                      +item.value,
                      validationMessage(item.message, `The minimum number is ${item.value}`),
                    );
                  case ValidationTypes.MAXNUMBER:
                    return maxNumberValidation(
                      +item.value,
                      validationMessage(item.message, `The maximum number is ${item.value}`),
                    );
                  case ValidationTypes.AGE:
                    return ageValidation(
                      api,
                      +item.min,
                      +item.max,
                      item.value,
                      validationMessage(item.message, `The age between ${item.min} and ${item.max} years`),
                    );
                  case ValidationTypes.NUMERIC:
                    return Validator.Numeric(validationMessage(item.message, `Only numeric`));
                  case ValidationTypes.PREG:
                    return Validator.Preg(
                      new RegExp(item.value.toString()),
                      validationMessage(item.message, 'Special characters are not allowed'),
                    );
                  case ValidationTypes.REQUIRED:
                    return Validator.Required(validationMessage(item.message, requiredMessage));
                  case ValidationTypes.MAXDATE:
                    return dateValidation({
                      api,
                      identifier1: identifier,
                      identifier2: item.value.toString(),
                      mode: DateComparisonMode.Greater,
                      message: validationMessage(item.message, 'Date should be less then end date'),
                    });
                  case ValidationTypes.MINDATE:
                    return dateValidation({
                      api,
                      identifier1: identifier,
                      identifier2: item.value.toString(),
                      mode: DateComparisonMode.Lower,
                      message: validationMessage(item.message, 'Date should be more then start date'),
                    });
                  default:
                    break;
                }

                return null;
              }),
            ]
          : []),
      ],
    }),
    [requiredMessage],
  );

  const setDefaultValidation = useCallback(
    (newQuestions: Question[], api: FormApiInterface) => {
      let documentsToQuestion = {};

      const docUploadsQuestions = newQuestions.filter(
        ({ field_type: type, required, hidden }) =>
          type === questionFieldType.DOC_UPLOAD && required === QuestionRequired.YES && !hidden,
      );
      docUploadsQuestions.forEach(({ identifier }) => {
        documentsToQuestion = {
          ...documentsToQuestion,
          [identifier]: [],
          [`${identifier}_required`]: [Validator.Required(requiredMessage)],
        };
      });

      const dialogBoxValidations = {};
      const dialogBoxQuestions = newQuestions.filter(({ field_type: type }) => type === questionFieldType.DIALOG_BOX);
      dialogBoxQuestions.forEach(({ required, identifier, hidden }) => {
        if (required === QuestionRequired.YES && !hidden) {
          dialogBoxValidations[`${identifier}_required`] = [Validator.Required(requiredMessage)];
        }
      });

      setValidation({
        ...newQuestions.reduce((result, question) => {
          if (question) {
            const { hidden, field_type: type } = question;
            return {
              ...result,
              ...(!hidden && type !== questionFieldType.LABEL && questionValidation(question, api)),
            };
          }
          return {
            ...result,
          };
        }, {}),
        ...documentsToQuestion,
        ...dialogBoxValidations,
      });
    },
    [setValidation, questionValidation, requiredMessage],
  );

  const resetUserQuestions = useCallback(
    (api: FormApiInterface) => {
      const newQuestions = composeQuestions(questions, api);
      setUserQuestions(newQuestions);
      cleanupValues(newQuestions, api);
      setDefaultValidation(newQuestions, api);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, cleanupValues],
  );

  const steps: FieldGroupsData = useMemo(() => newFormatData({ questions: userQuestions, personaQuestion, lang }), [
    userQuestions,
    personaQuestion,
    lang,
  ]);

  return {
    eventQuestion,
    steps,
    pregnancyValues,
    setPregnancyValues,
    documents,
    setDocuments,
    validation,
    setValidation,
    questionValidation,
    userQuestions,
    setUserQuestions,
    resetUserQuestions,
    submitted,
    setSubmitted,
    pregnancyFile,
    setPregnancyFile,
  };
};
